import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { MembershipStatus, Role } from '@wasity/shared';
import { Button, Card, Chip, EmptyState, ErrorNotice, Field, Input, Notice, PageHeader, Pagination, Select, Skeleton, Table, TabPanel, Tabs, Toolbar } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { ApiError, api, qs } from '@/lib/api';
import { errorText } from '@/lib/errors';
import { phoneDisplay } from '@/lib/format';
import type { UserRow } from '@/lib/types';
import { realEmail, useRegions } from './common';

type Tab = 'broker' | 'evaluator' | 'user' | 'admin';
const LIMIT = 50;

/** A member's standing: the tier while active, the state word otherwise. Used by the list (profile) and the account page (broker/evaluator record). */
export function MembershipStatusChip({ status, tier }: { status: MembershipStatus; tier: { nameAr: string; nameEn: string } | null }) {
  const { t, locale } = useI18n();
  if (status === 'past_due') return <Chip form="warning">{t('status.pastDue')}</Chip>;
  if (status === 'active' && tier) return <Chip form="outlined">{locale === 'ar' ? tier.nameAr : tier.nameEn}</Chip>;
  if (status === 'cancelled') return <Chip form="muted">{t('status.cancelledMembership')}</Chip>;
  if (status === 'expired') return <Chip form="muted">{t('status.expiredMembership')}</Chip>;
  return <Chip form="muted">{t('status.noMembership')}</Chip>;
}
export function MembershipChip({ u }: { u: UserRow }) {
  const p = u.profile;
  if (!p) return <span className="text-muted">—</span>;
  return <MembershipStatusChip status={p.membershipStatus} tier={p.tier} />;
}
export function AccountChip({ u }: { u: UserRow }) {
  const { t } = useI18n();
  if (u.banned) return <Chip form="muted">{t('status.suspended')}</Chip>;
  if (u.mustChangePassword && !u.lastLoginAt) return <Chip form="mint">{t('status.newAccount')}</Chip>;
  return <Chip form="filled">{t('status.active')}</Chip>;
}

export function AdminUsers() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('broker');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [form, setForm] = useState({ role: 'broker' as 'broker' | 'evaluator', name: '', email: '', phone: '', licenceNo: '', licenceExpiry: '', crNumber: '', regionId: '' });
  const [created, setCreated] = useState<{ email: string; sent: boolean } | null>(null);
  const list = useQuery({ queryKey: ['admin', 'users', tab, q, offset], queryFn: () => api.get<{ items: UserRow[] }>(`/api/admin/users${qs({ role: tab, q, limit: LIMIT, offset })}`) });
  const regions = useRegions();
  const create = useMutation({
    mutationFn: () => api.post<{ id: string; email: string; inviteEmailSent: boolean }>('/api/admin/users', { role: form.role, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() || undefined, licenceNo: form.licenceNo.trim() || undefined, licenceExpiry: form.licenceExpiry || undefined, crNumber: form.crNumber.trim() || undefined, regionId: form.regionId || undefined }),
    onSuccess: (r) => {
      setCreated({ email: r.email, sent: r.inviteEmailSent });
      setForm({ role: form.role, name: '', email: '', phone: '', licenceNo: '', licenceExpiry: '', crNumber: '', regionId: '' });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const err = create.error instanceof ApiError ? create.error : null;
  const staff = tab === 'broker' || tab === 'evaluator';
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.users') }]} title={t('nav.users')} />
      <Tabs<Tab> id="admin-users" value={tab} onChange={(k) => { setTab(k); setOffset(0); }} items={[{ key: 'broker', label: t('admin.users.tabs.brokers') }, { key: 'evaluator', label: t('admin.users.tabs.evaluators') }, { key: 'user', label: t('admin.users.tabs.users') }, { key: 'admin', label: t('admin.users.tabs.admins') }]} />
      <TabPanel id="admin-users" value={tab}>
        <div className="grid lg:grid-cols-[minmax(0,1.8fr)_minmax(300px,1fr)] gap-6 items-start">
          <div className="flex flex-col gap-3">
            <Toolbar>
              <Input className="w-[280px] max-w-full" placeholder={t('admin.users.searchPlaceholder')} value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} aria-label={t('app.search')} />
            </Toolbar>
            {list.isLoading ? <Skeleton lines={8} /> : list.error ? <ErrorNotice error={list.error} retry={() => void list.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : list.data?.items.length ? (
              <Card className="p-0 overflow-hidden">
                <Table className="border-0 rounded-none">
                  <thead>
                    <tr>
                      <th>{tab === 'broker' ? `${t('app.name')} · ${t('admin.users.office')}` : t('app.name')}</th>
                      {staff ? <th>{t('app.district')}</th> : <th>{t('app.phone')}</th>}
                      {staff ? <th>{t('admin.users.membership')}</th> : <th>{t('app.email')}</th>}
                      {staff ? <th>{t('app.slots')}</th> : <th>{t('app.since')}</th>}
                      <th>{t('app.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.data.items.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <Link to={`/admin/users/${u.id}`} className="whitespace-nowrap">{u.name}</Link>
                          {tab === 'broker' ? <div className={`text-[13px] mt-0.5 ${u.profile?.officeName ? 'text-fg-2' : 'text-muted'}`}>{u.profile?.officeName ?? t('admin.users.independent')}</div> : null}
                        </td>
                        {staff ? <td>{u.region ?? '—'}</td> : <td className="ltr num">{phoneDisplay(u.phoneNumber)}</td>}
                        {staff ? <td><MembershipChip u={u} /></td> : <td className="ltr num">{realEmail(u.email) ?? '—'}</td>}
                        {staff ? <td className="num">{u.profile?.slots ? `${u.profile.slots.used} / ${u.profile.slots.max}` : '—'}</td> : <td className="text-muted text-[13px] whitespace-nowrap">{formatRelative(u.createdAt, locale)}</td>}
                        <td><AccountChip u={u} /></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            ) : <EmptyState title={t('app.noResults')} />}
            <Pagination offset={offset} limit={LIMIT} count={list.data?.items.length ?? 0} onChange={setOffset} labels={{ prev: t('app.prev'), next: t('app.next'), page: t('app.page') }} />
          </div>
          <aside className="rounded-sm border border-line-2 bg-bg p-6 lg:sticky lg:top-6">
            <p className="label">{t('admin.users.newAccount')}</p>
            <h2 className="mt-1 mb-4">{t('admin.users.brokerOrEvaluator')}</h2>
            {created ? <Notice tone={created.sent ? 'success' : 'warning'} className="mb-4">{t(created.sent ? 'admin.users.created' : 'admin.users.createdNoEmail', { email: created.email })}</Notice> : null}
            {err ? <Notice tone="error" className="mb-4">{errorText(err, t)}</Notice> : null}
            <form onSubmit={submit} className="flex flex-col gap-4">
              <Field label={t('admin.users.kind')}>
                <div className="flex gap-2">
                  {(['broker', 'evaluator'] as const).map((r) => (
                    <label key={r} className={`flex-1 flex items-center gap-2 px-3.5 py-3 rounded-sm border cursor-pointer ${form.role === r ? 'border-accent bg-bg-2' : 'border-line'}`}>
                      <input type="radio" name="role" className="sr-only" checked={form.role === r} onChange={() => setForm({ ...form, role: r })} />
                      <span className={`size-4 rounded-full border flex-none ${form.role === r ? 'border-[5px] border-accent' : 'border-input-line'}`} aria-hidden />
                      {t(`roles.${r as Role}`)}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label={t('app.name')} htmlFor="nu-name"><Input id="nu-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label={t('app.email')} htmlFor="nu-email"><Input id="nu-email" type="email" dir="ltr" className="num" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label={t('app.phone')} htmlFor="nu-phone"><Input id="nu-phone" type="tel" dir="ltr" className="num" placeholder="05XXXXXXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('admin.users.licenceNo')} htmlFor="nu-lic"><Input id="nu-lic" dir="ltr" className="num" value={form.licenceNo} onChange={(e) => setForm({ ...form, licenceNo: e.target.value })} /></Field>
                <Field label={t('admin.users.licenceExpiry')} htmlFor="nu-exp"><Input id="nu-exp" type="date" dir="ltr" className="num" value={form.licenceExpiry} onChange={(e) => setForm({ ...form, licenceExpiry: e.target.value })} /></Field>
              </div>
              <Field label={t('admin.users.crNumber')} htmlFor="nu-cr"><Input id="nu-cr" dir="ltr" className="num" value={form.crNumber} onChange={(e) => setForm({ ...form, crNumber: e.target.value })} /></Field>
              <Field label={t('admin.users.region')} htmlFor="nu-region">
                <Select id="nu-region" value={form.regionId} onChange={(e) => setForm({ ...form, regionId: e.target.value })}>
                  <option value="">—</option>
                  {regions.data?.items.map((r) => <option key={r.id} value={r.id}>{locale === 'ar' ? r.nameAr : r.nameEn}</option>)}
                </Select>
              </Field>
              <Button type="submit" variant="primary" className="w-full" loading={create.isPending}>{t('admin.users.createAndSend')}</Button>
              <p className="text-muted text-[13px]">{t('admin.users.createHint')}</p>
            </form>
          </aside>
        </div>
      </TabPanel>
    </div>
  );
}
